import { api } from "@/domains/shared/services/api";

export type AdminEmailConversationStatus = "open" | "closed";
export type AdminEmailMessageDirection = "inbound" | "outbound";

export type AdminEmailConversationListItem = {
    id: string;
    tenantId?: string | null;
    userId: string;
    userName: string;
    userEmail: string;
    subject: string;
    status: AdminEmailConversationStatus;
    unreadCount: number;
    lastMessageAt?: string | null;
    lastMessagePreview?: string | null;
    lastMessageDirection?: AdminEmailMessageDirection | null;
    createdAt: string;
    createdByAdminId?: string | null;
    closedAt?: string | null;
};

export type AdminEmailInboxMessage = {
    id: string;
    direction: AdminEmailMessageDirection;
    status: string;
    from: string;
    to: string[];
    cc?: string[];
    subject: string;
    textBody?: string | null;
    htmlBody?: string | null;
    sentAt?: string | null;
    receivedAt?: string | null;
    createdAt: string;
    sentByAdminId?: string | null;
    failureReason?: string | null;
};

export type AdminEmailConversationDetail = {
    id: string;
    tenantId?: string | null;
    userId: string;
    userName: string;
    userEmail: string;
    subject: string;
    status: AdminEmailConversationStatus;
    unreadCount: number;
    replyToAddress: string;
    createdAt: string;
    createdByAdminId?: string | null;
    closedAt?: string | null;
    messages: AdminEmailInboxMessage[];
};

export type AdminEmailUnresolvedItem = {
    id: string;
    receivedAt: string;
    from: string;
    subject: string;
    textPreview?: string | null;
    provider: string;
    providerMessageId: string;
    resolutionFailureReason: string;
};

export type AdminEmailInboxListResponse = {
    items: AdminEmailConversationListItem[];
    total: number;
    skip: number;
    take: number;
};

export type AdminEmailUnresolvedListResponse = {
    items: AdminEmailUnresolvedItem[];
    total: number;
    skip: number;
    take: number;
};

export type AdminEmailInboxListParams = {
    search?: string;
    status?: AdminEmailConversationStatus;
    userId?: string;
    unreadOnly?: boolean;
    skip?: number;
    take?: number;
};

export async function fetchAdminEmailConversations(params: AdminEmailInboxListParams = {}) {
    const { data } = await api.get<AdminEmailInboxListResponse>("/admin/email/conversations", { params });
    return data;
}

export async function fetchAdminEmailConversation(conversationId: string) {
    const { data } = await api.get<AdminEmailConversationDetail>(`/admin/email/conversations/${conversationId}`);
    return data;
}

export async function createAdminEmailConversation(payload: {
    userId: string;
    subject: string;
    textBody?: string | null;
    htmlBody?: string | null;
}) {
    const { data } = await api.post<{ conversationId: string; messageId: string; status: string }>(
        "/admin/email/conversations",
        payload,
    );
    return data;
}

export async function replyAdminEmailConversation(
    conversationId: string,
    payload: { textBody?: string | null; htmlBody?: string | null },
) {
    const { data } = await api.post<{ messageId: string; status: string }>(
        `/admin/email/conversations/${conversationId}/messages`,
        payload,
    );
    return data;
}

export async function closeAdminEmailConversation(conversationId: string) {
    const { data } = await api.post<{ id: string; status: string; closedAt?: string | null }>(
        `/admin/email/conversations/${conversationId}/close`,
    );
    return data;
}

export async function reopenAdminEmailConversation(conversationId: string) {
    const { data } = await api.post<{ id: string; status: string; closedAt?: string | null }>(
        `/admin/email/conversations/${conversationId}/reopen`,
    );
    return data;
}

export async function fetchAdminEmailUnreadSummary() {
    const { data } = await api.get<{ totalUnread: number; conversationsWithUnread: number }>(
        "/admin/email/unread-summary",
    );
    return data;
}

export async function fetchAdminEmailUnresolved(params: { skip?: number; take?: number } = {}) {
    const { data } = await api.get<AdminEmailUnresolvedListResponse>("/admin/email/unresolved", { params });
    return data;
}

export const ADMIN_EMAIL_ANNOUNCEMENT_SEGMENTS = [
    { id: "therapists_active", label: "Terapeutas ativos" },
    { id: "therapists_trial", label: "Terapeutas em trial" },
    { id: "therapists_trial_expired", label: "Terapeutas com trial expirado" },
    { id: "therapists_cancelled", label: "Terapeutas cancelados" },
    { id: "clinic_managers_active", label: "Gestores de clínica ativos" },
    { id: "clinic_managers_cancelled", label: "Gestores de clínica cancelados" },
    { id: "secretaries_active", label: "Secretárias ativas" },
    { id: "parents_active", label: "Responsáveis ativos" },
    { id: "parents_all", label: "Todos os responsáveis" },
] as const;

export type AdminEmailAnnouncementSegmentId = (typeof ADMIN_EMAIL_ANNOUNCEMENT_SEGMENTS)[number]["id"];

export type AdminEmailAnnouncementPreviewResponse = {
    segments: { id: string; count: number }[];
    total: number;
};

export type AdminEmailAnnouncementSendResponse = {
    campaignId: string;
    total: number;
    skipped: number;
    perSegment: { id: string; count: number }[];
};

export async function previewAdminEmailCampaign(payload: { segments: string[] }) {
    const { data } = await api.post<AdminEmailAnnouncementPreviewResponse>(
        "/admin/email/campaigns/preview",
        payload,
    );
    return data;
}

export async function sendAdminEmailCampaign(payload: {
    segments: string[];
    subject: string;
    textBody?: string | null;
    htmlBody?: string | null;
}) {
    const { data } = await api.post<AdminEmailAnnouncementSendResponse>("/admin/email/campaigns", payload);
    return data;
}
